import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { formatCurrency } from '@/services/order-service';
import { OrderItemResponse, OrderResponse } from '@/types/order-type';
import { calculateOrderTotals } from '@/utils/calc-order';
import { useMemo } from 'react';
import { OrderStatusBadge } from '../sale/OrderStatusBadge';

interface PublicOrderSummaryProps {
  order: OrderResponse;
  orderItems: OrderItemResponse[];
}

export function PublicOrderSummary({ order, orderItems }: PublicOrderSummaryProps) {
  const { subtotal, shippingFee, discount, tax, total } = useMemo(() => {
    return calculateOrderTotals({
      items: orderItems,
      deliveryFee: Number(order.deliveryFee ?? 2),
      discountPercentage: Number(order.discountPercentage ?? 0),
      taxRate: 0,
    });
  }, [orderItems, order.deliveryFee, order.discountPercentage]);

  return (
    <Card className="border border-slate-200 shadow-sm rounded-xl">
      <CardHeader className="py-3 px-4">
        <h3 className="text-sm font-semibold text-slate-800">Order Summary</h3>
      </CardHeader>

      <CardContent className="px-4 pb-4">
        <div className="space-y-2">
          <Row label="Subtotal" value={formatCurrency(String(subtotal))} />
          {discount > 0 && (
            <Row
              label="Discount"
              value={`-${formatCurrency(String(discount))}`}
              valueClass="text-emerald-600"
            />
          )}
          <Row label="Shipping" value={formatCurrency(String(shippingFee))} />
          <Row label="Tax (0%)" value={formatCurrency(String(tax))} />

          <Separator className="my-3" />

          <div className="flex items-center justify-between rounded-lg border-2 border-blue-200 bg-slate-50 px-3 py-2">
            <span className="text-sm font-semibold text-slate-800">Total</span>
            <span className="text-lg font-bold text-blue-700">{formatCurrency(String(total))}</span>
          </div>
        </div>

        <div className="mt-4 border-t border-slate-200 pt-3">
          <div className="grid grid-cols-2 gap-y-1 text-xs text-slate-500">
            <span className="font-bold">Order No</span>
            <span className="text-[11px] font-medium text-right text-gray-600">
              #{order.orderNumber || order.id}
            </span>

            <span className="font-bold">Items</span>
            <span className="text-right font-bold">{orderItems.length}</span>

            <span className="font-bold">Status</span>
            <span className="text-right font-bold">
              <OrderStatusBadge status={order.status} />
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  valueClass = '',
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <span className={`text-sm font-semibold text-slate-800 ${valueClass}`}>{value}</span>
    </div>
  );
}
